/**
 * =============================================================================
 * RFI LINK PICKER COMPONENT
 * =============================================================================
 *
 * Presentational picker (scoped to ONE RFI) for linking an APPROVED-GENERAL
 * reply as evidence against one or more of that RFI's items. The panel owns the
 * candidate-reply loader and the `linkResponses` action and threads both down —
 * the picker NEVER fetches RFIs itself (no duplicate GET /rfi).
 *
 * It selects a reply → target items → optional `valueText` per item → optional
 * photo-only `attachmentId`. Per ADR-022 §14 D5 (owner decision 2026-07-10;
 * adjacent-thumbnail picker design signed off 2026-07-12) the
 * attachment picker filters to RFI-LINKABLE images only (APPROVED + not
 * legal-held, via the shared `isRfiLinkableAdminMedia` rule) and labels each by
 * position + `originalFilename` with an adjacent authenticated `AdminImageThumbnail`
 * preview — moderators no longer pick legal evidence from opaque `att_…` ids.
 * The backend independently re-validates eligibility on the single write
 * chokepoint (`RfiService.linkResponses` — 400 on any violation), so this filter
 * is a UX affordance, never the security control.
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Checkbox, Flex, FlexItem, Text, Textarea } from '@bigcommerce/big-design';
import { CONTENT_VALIDATION, isRfiLinkableAdminMedia } from '@find-my-part/types';
import type {
  AdminRfiItem,
  CreateRfiResponseLinkInput,
  RfiResponseLinkTarget,
} from '@find-my-part/types';
import { formatDate } from '../../../utils/formatters';
import { buildReplyExcerpt, RFI_ITEM_TYPE_LABELS } from './rfiLabels';
import type { UseRfiCandidateRepliesResult } from '../../../hooks/useRfiCandidateReplies';
import { AdminImageThumbnail } from '../../Media/AdminImageThumbnail';
import { tokens } from '../../../tokens';

/**
 * Props for RfiLinkPicker.
 */
export interface RfiLinkPickerProps {
  /** The RFI being linked into (owns the `link:${rfiId}` pending key). */
  rfiId: string;
  /** Auth token for the adjacent authenticated image previews (ADR-022 §14 D5). */
  token: string;
  /** This RFI's items (link targets), already ordinal-sorted by the caller. */
  items: AdminRfiItem[];
  /** The shared candidate-reply loader (APPROVED-GENERAL replies). */
  candidates: UseRfiCandidateRepliesResult;
  /** Set of `${replyId}::${rfiItemId}` pairs already linked (disables duplicates). */
  alreadyLinkedPairs: Set<string>;
  /** Whether a link submission for this RFI is in flight. */
  isSubmitting: boolean;
  /** Link action (bound to this RFI by the parent). Resolves true on success. */
  onLink: (input: CreateRfiResponseLinkInput) => Promise<boolean>;
  /** Close the picker. */
  onClose: () => void;
  /** Optional test ID prefix. */
  testId?: string;
}

/** Compose the dedup key for a (reply, item) pair. */
function linkPairKey(replyId: string, rfiItemId: string): string {
  return `${replyId}::${rfiItemId}`;
}

/**
 * Picker for linking a reply's evidence to one or more RFI items.
 */
export function RfiLinkPicker({
  rfiId,
  token,
  items,
  candidates,
  alreadyLinkedPairs,
  isSubmitting,
  onLink,
  onClose,
  testId,
}: RfiLinkPickerProps): React.ReactElement {
  const [selectedReplyId, setSelectedReplyId] = useState<string | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [valueTextByItem, setValueTextByItem] = useState<Record<string, string>>({});
  const [attachmentByItem, setAttachmentByItem] = useState<Record<string, string>>({});

  const { fetchCandidates } = candidates;

  // Lazy-load candidates when the picker opens.
  useEffect(() => {
    void fetchCandidates();
  }, [fetchCandidates]);

  const resetTargets = useCallback(() => {
    setSelectedItemIds(new Set());
    setValueTextByItem({});
    setAttachmentByItem({});
  }, []);

  // Clear the selection (and its targets) if the selected reply is no longer in
  // the candidate cache — e.g. after a refresh dropped/replaced it (D-2).
  useEffect(() => {
    if (selectedReplyId && !candidates.replies.some((r) => r.replyId === selectedReplyId)) {
      setSelectedReplyId(null);
      resetTargets();
    }
  }, [candidates.replies, selectedReplyId, resetTargets]);

  const handleSelectReply = useCallback(
    (replyId: string) => {
      setSelectedReplyId(replyId);
      resetTargets();
    },
    [resetTargets]
  );

  const toggleItem = useCallback((rfiItemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(rfiItemId)) {
        next.delete(rfiItemId);
      } else {
        next.add(rfiItemId);
      }
      return next;
    });
  }, []);

  const selectedReply = candidates.replies.find((r) => r.replyId === selectedReplyId) ?? null;

  const canSubmit = selectedReplyId !== null && selectedItemIds.size > 0 && !isSubmitting;

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (selectedReplyId === null || selectedItemIds.size === 0 || isSubmitting) return;

    const links: RfiResponseLinkTarget[] = [];
    for (const item of items) {
      if (!selectedItemIds.has(item.rfiItemId)) continue;
      const target: RfiResponseLinkTarget = { rfiItemId: item.rfiItemId };
      const valueText = (valueTextByItem[item.rfiItemId] ?? '').trim();
      if (valueText) {
        target.valueText = valueText;
      }
      const attachmentId = attachmentByItem[item.rfiItemId];
      // Only submit an attachment that is STILL RFI-linkable on the current
      // candidate view: a "Refresh replies" between selection and submit can turn
      // a previously-selected image ineligible (its radio disappears), leaving a
      // stale id in `attachmentByItem`. The backend rejects it regardless, but
      // don't submit a selection whose option is no longer offered.
      if (
        item.itemType === 'photo' &&
        attachmentId &&
        (selectedReply?.images.some(
          (img) => img.attachmentId === attachmentId && isRfiLinkableAdminMedia(img)
        ) ??
          false)
      ) {
        target.attachmentId = attachmentId;
      }
      links.push(target);
    }

    const succeeded = await onLink({ replyId: selectedReplyId, links });
    if (succeeded) {
      onClose();
    }
  }, [
    selectedReplyId,
    selectedReply,
    selectedItemIds,
    isSubmitting,
    items,
    valueTextByItem,
    attachmentByItem,
    onLink,
    onClose,
  ]);

  return (
    <Box
      marginTop="small"
      padding="small"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.md, backgroundColor: tokens.color.surface.subtle }}
      data-testid={testId}
    >
      <Flex alignItems="center" justifyContent="space-between" marginBottom="xSmall">
        <FlexItem>
          <Text bold>
            Add evidence
          </Text>
        </FlexItem>
        <FlexItem>
          <Button
            variant="subtle"
            onClick={onClose}
            data-testid={testId ? `${testId}-close` : undefined}
          >
            Cancel
          </Button>
        </FlexItem>
      </Flex>

      {/* Candidate reply selector */}
      {candidates.isLoading && candidates.replies.length === 0 ? (
        <Text
          color="secondary"
          aria-busy="true"
          data-testid={testId ? `${testId}-candidates-loading` : undefined}
        >
          Loading approved replies…
        </Text>
      ) : candidates.error ? (
        <Box data-testid={testId ? `${testId}-candidates-error` : undefined}>
          <Text color="secondary">{candidates.error}</Text>
          <Button
            variant="subtle"
            onClick={() => candidates.refresh()}
            data-testid={testId ? `${testId}-candidates-retry` : undefined}
          >
            Retry
          </Button>
        </Box>
      ) : candidates.replies.length === 0 ? (
        <Text color="secondary" data-testid={testId ? `${testId}-candidates-empty` : undefined}>
          No approved replies are available to link yet.
        </Text>
      ) : (
        <Box
          role="radiogroup"
          aria-label="Approved replies to link"
          data-testid={testId ? `${testId}-candidates` : undefined}
        >
          {candidates.replies.map((reply) => (
            <Box
              as="label"
              key={reply.replyId}
              marginBottom="xSmall"
              style={{ display: 'block', cursor: 'pointer' }}
            >
              <Flex flexGap="0.5rem" alignItems="flex-start">
                <FlexItem>
                  <input
                    type="radio"
                    name={`rfi-candidate-${rfiId}`}
                    checked={selectedReplyId === reply.replyId}
                    onChange={() => handleSelectReply(reply.replyId)}
                    aria-label={`Select reply by ${reply.authorName}`}
                    data-testid={
                      testId ? `${testId}-candidate-${reply.replyId}` : undefined
                    }
                  />
                </FlexItem>
                <FlexItem>
                  <Text>
                    <strong>{reply.authorName}</strong>
                    {' · '}
                    {formatDate(reply.createdAt)}
                  </Text>
                  <Text color="secondary">
                    {buildReplyExcerpt(reply.body)}
                  </Text>
                </FlexItem>
              </Flex>
            </Box>
          ))}

          {candidates.hasMore && (
            <Button
              variant="subtle"
              onClick={() => candidates.loadMore()}
              isLoading={candidates.isFetchingMore}
              disabled={candidates.isFetchingMore}
              data-testid={testId ? `${testId}-load-more` : undefined}
            >
              Load more
            </Button>
          )}
          <Button
            variant="subtle"
            onClick={() => candidates.refresh()}
            disabled={candidates.isLoading}
            data-testid={testId ? `${testId}-refresh-candidates` : undefined}
          >
            Refresh replies
          </Button>
        </Box>
      )}

      {/* Item-target selector (after a reply is selected) */}
      {selectedReply && (
        <Box marginTop="small" data-testid={testId ? `${testId}-targets` : undefined}>
          <Text bold>
            Link to items
          </Text>
          {items.map((item) => {
            const alreadyLinked = alreadyLinkedPairs.has(
              linkPairKey(selectedReply.replyId, item.rfiItemId)
            );
            const isChecked = selectedItemIds.has(item.rfiItemId);
            return (
              <Box
                key={item.rfiItemId}
                marginTop="xSmall"
                data-testid={testId ? `${testId}-target-${item.rfiItemId}` : undefined}
              >
                <Checkbox
                  label={
                    alreadyLinked
                      ? `${item.label} (already linked)`
                      : `${item.label} (${RFI_ITEM_TYPE_LABELS[item.itemType]})`
                  }
                  checked={isChecked}
                  disabled={alreadyLinked}
                  onChange={() => toggleItem(item.rfiItemId)}
                  data-testid={testId ? `${testId}-target-${item.rfiItemId}-checkbox` : undefined}
                />
                {isChecked && (
                  <Box marginTop="xSmall" marginLeft="medium">
                    <Textarea
                      label="Value (optional)"
                      rows={2}
                      maxLength={CONTENT_VALIDATION.MAX_RFI_RESPONSE_VALUE_TEXT_LENGTH}
                      value={valueTextByItem[item.rfiItemId] ?? ''}
                      onChange={(e) =>
                        setValueTextByItem((prev) => ({
                          ...prev,
                          [item.rfiItemId]: e.target.value,
                        }))
                      }
                      placeholder="Optional staff-recorded value"
                      aria-label={`Value for ${item.label}`}
                      data-testid={testId ? `${testId}-target-${item.rfiItemId}-value` : undefined}
                    />
                    {item.itemType === 'photo' &&
                      (() => {
                        // Only RFI-LINKABLE images are selectable (ADR-022 §14 D5):
                        // APPROVED + not legal-held, via the SHARED status rule the
                        // backend also enforces at the write chokepoint. Each option
                        // carries an adjacent authenticated preview + a positional
                        // filename label so evidence is never picked from opaque ids.
                        const eligibleImages = selectedReply.images.filter((img) =>
                          isRfiLinkableAdminMedia(img)
                        );
                        const rawSelectedId = attachmentByItem[item.rfiItemId] ?? '';
                        // Derive the DISPLAYED selection from current eligibility: a
                        // "Refresh replies" can turn a previously-selected image
                        // ineligible (its radio disappears) while a stale id lingers
                        // in `attachmentByItem`, which would otherwise render the
                        // radiogroup with nothing checked. Fall back to the "No
                        // attachment" default so the control stays coherent (the
                        // submit guard already drops the stale id; this reconciles
                        // only what the moderator SEES).
                        const selectedAttachmentId = eligibleImages.some(
                          (img) => img.attachmentId === rawSelectedId
                        )
                          ? rawSelectedId
                          : '';
                        const radioName = `rfi-attach-${rfiId}-${item.rfiItemId}`;
                        return (
                          <Box marginTop="xSmall">
                            {selectedReply.images.length === 0 ? (
                              <Text color="secondary">
                                This reply has no images to attach.
                              </Text>
                            ) : eligibleImages.length === 0 ? (
                              <Text
                                color="secondary"
                                data-testid={
                                  testId
                                    ? `${testId}-target-${item.rfiItemId}-no-eligible`
                                    : undefined
                                }
                              >
                                No approved images are available to attach.
                              </Text>
                            ) : (
                              <Box
                                role="radiogroup"
                                aria-label={`Attachment for ${item.label}`}
                                data-testid={
                                  testId
                                    ? `${testId}-target-${item.rfiItemId}-attachment`
                                    : undefined
                                }
                              >
                                <Text>
                                  Attachment (optional)
                                </Text>
                                <Box
                                  as="label"
                                  marginTop="xSmall"
                                  style={{ display: 'block', cursor: 'pointer' }}
                                >
                                  <Flex flexGap="0.5rem" alignItems="center">
                                    <FlexItem>
                                      <input
                                        type="radio"
                                        name={radioName}
                                        checked={selectedAttachmentId === ''}
                                        onChange={() =>
                                          setAttachmentByItem((prev) => ({
                                            ...prev,
                                            [item.rfiItemId]: '',
                                          }))
                                        }
                                        aria-label="No attachment"
                                        data-testid={
                                          testId
                                            ? `${testId}-target-${item.rfiItemId}-attachment-none`
                                            : undefined
                                        }
                                      />
                                    </FlexItem>
                                    <FlexItem>
                                      <Text>
                                        No attachment
                                      </Text>
                                    </FlexItem>
                                  </Flex>
                                </Box>
                                {eligibleImages.map((image, index) => {
                                  const label = `Photo ${index + 1}${
                                    image.originalFilename ? ` — ${image.originalFilename}` : ''
                                  }`;
                                  return (
                                    <Box
                                      as="label"
                                      key={image.attachmentId}
                                      marginTop="xSmall"
                                      style={{ display: 'block', cursor: 'pointer' }}
                                    >
                                      <Flex flexGap="0.5rem" alignItems="center">
                                        <FlexItem>
                                          <input
                                            type="radio"
                                            name={radioName}
                                            checked={selectedAttachmentId === image.attachmentId}
                                            onChange={() =>
                                              setAttachmentByItem((prev) => ({
                                                ...prev,
                                                [item.rfiItemId]: image.attachmentId,
                                              }))
                                            }
                                            aria-label={label}
                                            data-testid={
                                              testId
                                                ? `${testId}-target-${item.rfiItemId}-attachment-${image.attachmentId}`
                                                : undefined
                                            }
                                          />
                                        </FlexItem>
                                        <FlexItem>
                                          <AdminImageThumbnail
                                            src={image.url}
                                            alt={label}
                                            token={token}
                                            size={48}
                                            testId={
                                              testId
                                                ? `${testId}-target-${item.rfiItemId}-thumb-${image.attachmentId}`
                                                : undefined
                                            }
                                          />
                                        </FlexItem>
                                        <FlexItem>
                                          <Text>{label}</Text>
                                        </FlexItem>
                                      </Flex>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Flex justifyContent="flex-end" marginTop="small">
        <FlexItem>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            isLoading={isSubmitting}
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            Link evidence
          </Button>
        </FlexItem>
      </Flex>
    </Box>
  );
}
